import Modal from "@via/components/modal.tsx";
import { ButtonColor } from "@via/utils/constants.tsx";
import { GetUserGroupMemberResponse } from "common";
import { type FC, useEffect } from "react";
import { useDeleteUserGroupMember } from "@via/api/user-groups.api.tsx";
import { useDeleteInvitation } from "@via/pages/account/admin/admin.api.tsx";

type DeleteUserGroupMemberModalProps = {
  groupId: number;
  user: GetUserGroupMemberResponse;
  setUserToBeDeleted: (user: GetUserGroupMemberResponse | null) => void;
  isPending?: boolean;
};

const DeleteUserGroupMemberModal: FC<DeleteUserGroupMemberModalProps> = ({
  groupId,
  user,
  setUserToBeDeleted,
  isPending = false,
}) => {
  const {
    mutateAsync: deleteUser,
    isPending: isDeleteUserLoading,
    isSuccess: isDeleteUserSuccess,
  } = useDeleteUserGroupMember();

  const {
    mutateAsync: deleteInvitation,
    isPending: isDeleteInvitationLoading,
    isSuccess: isDeleteInvitationSuccess,
  } = useDeleteInvitation();

  const handleDeleteUser = async (
    userId: string,
    groupId: string
  ): Promise<void> => {
    if (userId) {
      if (isPending) {
        await deleteInvitation({ id: Number(userId) });
      } else {
        await deleteUser({ userId, groupId });
      }
    }
  };

  useEffect(() => {
    if (isDeleteUserSuccess || isDeleteInvitationSuccess) {
      setUserToBeDeleted(null);
    }
  }, [isDeleteUserSuccess, isDeleteInvitationSuccess]);

  return (
    <Modal
      open={!!user}
      setOpen={() => {
        setUserToBeDeleted(null);
      }}
      title={isPending ? "Delete Pending User" : "Delete User"}
      confirmText="Delete"
      confirmButtonColor={ButtonColor.Red}
      isConfirmLoading={isDeleteUserLoading || isDeleteInvitationLoading}
      onConfirm={() =>
        handleDeleteUser(user.id.toString(), groupId.toString())
      }>
      <div className="flex flex-col py-2 text-sm leading-6 text-slate-600 md:py-3 lg:py-4">
        {user?.name ? (
          <p>
            Are you sure you want to delete user{" "}
            <span className="font-semibold">{user.name}</span>, email{" "}
            <span className="font-semibold">{user.email}</span>?
          </p>
        ) : (
          <p>
            Are you sure you want to delete user with email{" "}
            <span className="font-semibold">{user?.email}</span>?
          </p>
        )}
      </div>
    </Modal>
  );
};

export default DeleteUserGroupMemberModal;
